import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import dynamic from 'next/dynamic';
import { LuCreditCard, LuLandmark, LuChevronDown, LuCheck } from 'react-icons/lu';
import PsePaymentPanel from './PsePaymentPanel';
import type { BillingInformation } from './BillingInfoForm';

const PayzenCardForm = dynamic(() => import('./PayzenCardForm'), { ssr: false });

export type PayMethod = 'card' | 'pse' | null;

const OPTIONS: {
  key: Exclude<PayMethod, null>;
  es: string;
  en: string;
  esFull: string;
  enFull: string;
}[] = [
  { key: 'card', es: 'Tarjeta', en: 'Card', esFull: 'Tarjeta de crédito o débito', enFull: 'Credit or debit card' },
  { key: 'pse', es: 'PSE', en: 'PSE', esFull: 'PSE · Débito bancario', enFull: 'PSE · Bank debit' },
];

/**
 * Selector de método como TABS ligeras (no tarjeta): fila horizontal con subrayado
 * naranja en el activo. Vive FUERA del contenedor blanco, debajo del título "Pago".
 * Solo cambia `payMethod` — no toca la lógica de pago/PSE.
 */
export function PaymentMethodTabs({
  payMethod,
  setPayMethod,
  isSpanish = true,
}: {
  payMethod: PayMethod;
  setPayMethod: (m: PayMethod) => void;
  isSpanish?: boolean;
}) {
  return (
    <div className="flex items-stretch gap-7 border-b border-slate-200/70">
      {OPTIONS.map((opt) => {
        const selected = payMethod === opt.key;
        const Icon = opt.key === 'card' ? LuCreditCard : LuLandmark;
        const label = isSpanish ? opt.es : opt.en;
        return (
          <button
            type="button"
            key={opt.key}
            onClick={() => setPayMethod(opt.key)}
            aria-pressed={selected}
            className={`-mb-px flex items-center gap-2 border-b-2 pb-2.5 pt-1 text-[14px] font-semibold transition-colors focus:outline-none focus-visible:text-tikipal-orange ${
              selected
                ? 'border-tikipal-orange text-slate-900'
                : 'border-transparent text-slate-400 hover:text-slate-600'
            }`}
          >
            <Icon className={`text-[18px] ${selected ? 'text-tikipal-orange' : 'text-slate-400'}`} />
            {label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Selector de método como DROPDOWN (estilo Stripe): un campo con relleno gris sutil que
 * muestra el método elegido (ícono + nombre) + chevron; al abrirlo lista Tarjeta/PSE.
 * Vive FUERA del contenedor, debajo de "Pago". Solo cambia `payMethod`.
 */
export function PaymentMethodDropdown({
  payMethod,
  setPayMethod,
  isSpanish = true,
}: {
  payMethod: PayMethod;
  setPayMethod: (m: PayMethod) => void;
  isSpanish?: boolean;
}) {
  const [open, setOpen] = useState(false);
  // El menú se renderiza en un PORTAL a document.body (position:fixed, z alto) porque los
  // campos de tarjeta de Krypton son iframes cross-origin que "atraviesan" un menú
  // absolute dentro del flujo; un portal al nivel de body sí los cubre.
  const [pos, setPos] = useState<{ left: number; top: number; width: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const current = OPTIONS.find((o) => o.key === payMethod) || null;
  const CurrentIcon = current ? (current.key === 'card' ? LuCreditCard : LuLandmark) : null;

  const toggle = () => {
    if (!open && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setPos({ left: r.left, top: r.bottom + 6, width: r.width });
    }
    setOpen((o) => !o);
  };

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex w-full items-center gap-3 rounded-[10px] px-3.5 py-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-tikipal-orange/40 ${
          open
            ? 'border border-slate-300 bg-white'
            : 'border border-transparent bg-[rgba(21,21,21,0.05)] hover:bg-[rgba(21,21,21,0.08)]'
        }`}
      >
        {CurrentIcon && <CurrentIcon className="shrink-0 text-[20px] text-slate-500" />}
        <span className={`text-[15px] font-medium ${current ? 'text-slate-900' : 'text-slate-400'}`}>
          {current
            ? isSpanish ? current.es : current.en
            : isSpanish ? 'Selecciona cómo pagar' : 'Choose how to pay'}
        </span>
        <LuChevronDown
          className={`ml-auto shrink-0 text-[20px] text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && pos && typeof document !== 'undefined' &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setOpen(false)} />
            <div
              role="listbox"
              style={{ position: 'fixed', left: pos.left, top: pos.top, width: pos.width }}
              className="z-[9999] overflow-hidden rounded-[12px] border border-slate-200/80 bg-white p-1.5 shadow-[0_18px_40px_-16px_rgba(15,23,42,0.28)]"
            >
              {OPTIONS.map((opt) => {
                const selected = payMethod === opt.key;
                const Icon = opt.key === 'card' ? LuCreditCard : LuLandmark;
                return (
                  <button
                    type="button"
                    key={opt.key}
                    role="option"
                    aria-selected={selected}
                    onClick={() => {
                      setPayMethod(opt.key);
                      setOpen(false);
                    }}
                    className={`flex w-full items-center gap-3 rounded-[8px] px-3 py-2.5 text-left text-[14px] text-[#151515] transition-colors hover:bg-slate-50 ${
                      selected ? 'bg-slate-100' : ''
                    }`}
                  >
                    <Icon className="shrink-0 text-[20px] text-slate-500" />
                    <span className="truncate">{isSpanish ? opt.esFull : opt.enFull}</span>
                    {selected && <LuCheck className="ml-auto shrink-0 text-[18px] text-slate-700" />}
                  </button>
                );
              })}
            </div>
          </>,
          document.body
        )}
    </div>
  );
}

type Props = {
  payMethod: PayMethod;
  /** Card formToken (already minted at billing submit). Only the CARD form uses it. */
  formToken: string;
  isSpanish?: boolean;
  billingInfo: BillingInformation;
  orderId: string;
  eventId: string;
  spaceId?: string;
  entranceId?: string;
  timeSlotId?: string;
  quantity: number;
};

/**
 * Contenido del método seleccionado (formulario de tarjeta Krypton O flujo PSE). El
 * caller lo coloca DENTRO del contenedor blanco y solo lo renderiza cuando hay método
 * elegido. Un solo Krypton montado a la vez (card ↔ PSE mutuamente excluyentes). NO
 * altera la integración de tarjeta ni la lógica/redirección de PSE.
 */
const PaymentMethodSelector = ({
  payMethod,
  formToken,
  isSpanish = true,
  billingInfo,
  orderId,
  eventId,
  spaceId,
  entranceId,
  timeSlotId,
  quantity,
}: Props) => {
  if (!payMethod) return null;
  return (
    <div className="tikipal-payzen-inline max-w-full min-w-0 overflow-x-clip">
      <style>{`
        /* === Layout safety (scoped) — DO NOT remove ===
           overflow-x: clip clips sideways WITHOUT forcing overflow-y to auto. */
        .tikipal-payzen-inline { max-width: 100%; min-width: 0; overflow-x: clip; }
        .tikipal-payzen-inline .kr-amount-wrapper { display: none !important; }
        .tikipal-payzen-inline #myPaymentForm,
        .tikipal-payzen-inline .kr-smart-form,
        .tikipal-payzen-inline .kr-smart-form-wrapper,
        .tikipal-payzen-inline .kr-embedded,
        .tikipal-payzen-inline .kr-form {
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          min-height: 0 !important;
          height: auto !important;
          box-sizing: border-box !important;
          overflow: visible !important;
        }
        .tikipal-payzen-inline iframe {
          max-width: 100% !important;
          width: 100% !important;
          box-sizing: border-box !important;
        }
        .tikipal-payzen-inline .kr-field,
        .tikipal-payzen-inline .kr-form-row,
        .tikipal-payzen-inline .kr-input-wrapper {
          max-width: 100% !important;
          min-width: 0 !important;
          box-sizing: border-box !important;
        }

        /* === Liquid-glass Tikipal polish (non-iframe wrappers only) === */
        .tikipal-payzen-inline .kr-card-header {
          background: rgba(255,255,255,0.7) !important;
          border: 1px solid rgb(226,232,240) !important;
          border-radius: 16px !important;
        }
        .tikipal-payzen-inline .kr-card-header-label {
          font-size: 13px !important;
          font-weight: 600 !important;
          letter-spacing: 0.01em !important;
          color: rgb(51,65,85) !important;
        }
        .tikipal-payzen-inline .kr-smart-form-list-section-name {
          font-size: 12px !important;
          font-weight: 600 !important;
          letter-spacing: 0.04em !important;
          color: rgb(100,116,139) !important;
          text-transform: none !important;
        }
        .tikipal-payzen-inline .kr-method-label,
        .tikipal-payzen-inline .kr-pse button {
          border: 1px solid rgb(226,232,240) !important;
          border-radius: 16px !important;
          background: rgba(255,255,255,0.85) !important;
          color: rgb(51,65,85) !important;
          min-height: 52px !important;
          box-shadow: 0 8px 24px -16px rgba(15,23,42,0.25) !important;
          transition: border-color .15s ease, box-shadow .15s ease, background .15s ease !important;
        }
        .tikipal-payzen-inline .kr-method-label:hover,
        .tikipal-payzen-inline .kr-pse button:hover {
          border-color: rgb(203,213,225) !important;
          box-shadow: 0 12px 30px -16px rgba(15,23,42,0.32) !important;
        }
        /* "Pagar ahora" = mismo tamaño/estilo que el botón "Comprar"
           (#billing-info-form-submit: w-full h-[44px] mt-1.5 rounded-[12px] bg-tikipal-orange, 14px, blanco). */
        .tikipal-payzen-inline .kr-payment-button {
          width: 100% !important;
          min-height: 44px !important;
          height: 44px !important;
          margin-top: 6px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          background: #FF6332 !important;
          border: none !important;
          border-radius: 12px !important;
          font-weight: 600 !important;
          font-size: 14px !important;
          letter-spacing: 0.01em !important;
          box-shadow: none !important;
        }

        /* === Compact CARD form (visual only) ===
           Tightens the vertical rhythm and puts MM/AA + CVV on ONE row.
           Targets ONLY merchant-DOM wrappers (.kr-field-element); the PAN /
           expiry / CVV inputs live inside Krypton's cross-origin iframes and are
           NEVER touched. Scoped to .kr-embedded so the PSE smart-form is unaffected. */
        .tikipal-payzen-inline .kr-embedded .kr-field-element { margin-bottom: 8px !important; }
        .tikipal-payzen-inline .kr-embedded .kr-field-element.kr-expiry,
        .tikipal-payzen-inline .kr-embedded .kr-field-element.kr-security-code {
          float: left !important;
          width: calc(50% - 6px) !important;
        }
        .tikipal-payzen-inline .kr-embedded .kr-field-element.kr-expiry { margin-right: 12px !important; }
        .tikipal-payzen-inline .kr-embedded .kr-field-element.kr-card-holder-name { clear: both !important; }
        .tikipal-payzen-inline .kr-embedded .kr-payment-button { clear: both !important; }

        /* === Campos estilo Stripe (visual) ===
           Relleno gris MUY sutil + plano + radio suave. Se pinta en los WRAPPERS de
           nuestro DOM (.kr-field-element) y se hace transparente lo interno para que el
           gris atraviese; los inputs PAN/MM/CVV siguen dentro de sus iframes PCI
           (contenido transparente), así que su placeholder queda sobre el gris sin tocar
           su seguridad ni su texto. */
        .tikipal-payzen-inline .kr-embedded .kr-field-element {
          background: #15151514 !important;
          border: 1px solid transparent !important;
          border-radius: 10px !important;
          box-shadow: none !important;
        }
        .tikipal-payzen-inline .kr-embedded .kr-field-component,
        .tikipal-payzen-inline .kr-embedded .kr-iframe-wrapper,
        .tikipal-payzen-inline .kr-embedded .kr-input-field,
        .tikipal-payzen-inline .kr-embedded .kr-select,
        .tikipal-payzen-inline .kr-embedded .kr-select-wrapper,
        .tikipal-payzen-inline .kr-embedded .kr-field-wrapper,
        .tikipal-payzen-inline .kr-embedded iframe {
          background: transparent !important;
          background-color: transparent !important;
        }
        .tikipal-payzen-inline .kr-embedded .kr-field-component {
          border-radius: 10px !important;
          padding: 6px 14px !important;
        }
        /* Texto de los campos editables (nombre/documento) al tamaño del billing (lux-input 15px). */
        .tikipal-payzen-inline .kr-embedded .kr-input-field {
          font-size: 15px !important;
        }
        /* Íconos de campo más pequeños, proporcionales al texto. */
        .tikipal-payzen-inline .kr-embedded .kr-icon,
        .tikipal-payzen-inline .kr-embedded .kr-select-caret {
          transform: scale(0.78) !important;
        }

        /* Blindaje del select "Tipo de documento": el campo va GRIS COMPLETO como los
           demás; y CUALQUIER elemento interno del campo (incluido un <select> NATIVO que
           Krypton usa en móvil, cuyo fondo blanco de iOS/Android no cede con una regla
           puntual) va transparente + appearance:none, para que no quede una "barra blanca"
           dentro. Se excluye .kr-options/.kr-option → el panel desplegado sigue blanco. */
        /* Controles nativos (p.ej. el <select> de iOS) en ESQUEMA CLARO → el menú nativo
           del "Tipo de documento" se pinta claro, no en material oscuro. */
        .tikipal-payzen-inline,
        .tikipal-payzen-inline .kr-embedded,
        .tikipal-payzen-inline .kr-embedded select {
          color-scheme: light !important;
        }
        .tikipal-payzen-inline .kr-embedded .kr-field-element.kr-identity-document-type {
          background: #15151514 !important;
        }
        .tikipal-payzen-inline .kr-embedded .kr-identity-document-type *:not(.kr-options):not(.kr-option),
        .tikipal-payzen-inline .kr-embedded .kr-identity-document-type select,
        .tikipal-payzen-inline .kr-embedded .kr-identity-document-type input {
          background: transparent !important;
          background-color: transparent !important;
          background-image: none !important;
          box-shadow: none !important;
          border-color: transparent !important;
          color: #151515 !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          appearance: none !important;
        }
        /* El TEXTO visible del select de documento (placeholder/valor) al MISMO tamaño que los
           inputs (titular/documento) → tipografia consistente. En iOS el placeholder lo pinta
           un span .kr-select-placeholder (quedaba en 13px). 15px base (desktop); el media-query
           anti-zoom lo sube a 16px en movil. */
        .tikipal-payzen-inline .kr-embedded .kr-identity-document-type .kr-select-placeholder,
        .tikipal-payzen-inline .kr-embedded .kr-identity-document-type .kr-selected-option {
          font-size: 15px !important;
        }

        /* === Dropdown "Tipo de documento" (kr-custom-select, DOM propio) ===
           Panel + opciones pulidas, mismo lenguaje que el selector de método. */
        .tikipal-payzen-inline .kr-embedded .kr-options {
          background: #fff !important;
          border: 1px solid rgb(226,232,240) !important;
          border-radius: 12px !important;
          box-shadow: 0 18px 40px -16px rgba(15,23,42,0.28) !important;
          padding: 6px !important;
          margin-top: 6px !important;
          max-height: 280px !important;
          overflow-y: auto !important;
          z-index: 30 !important;
        }
        .tikipal-payzen-inline .kr-embedded .kr-option {
          border-radius: 8px !important;
          padding: 10px 12px !important;
          min-height: 0 !important;
          font-size: 14px !important;
          line-height: 1.2 !important;
          color: rgb(51,65,85) !important;
          cursor: pointer !important;
          transition: background .12s ease !important;
        }
        .tikipal-payzen-inline .kr-embedded .kr-option:hover {
          background: rgb(248,250,252) !important;
        }
        .tikipal-payzen-inline .kr-embedded .kr-option.kr-active-option,
        .tikipal-payzen-inline .kr-embedded .kr-option[aria-selected="true"] {
          background: rgb(241,245,249) !important;
          font-weight: 600 !important;
          color: rgb(15,23,42) !important;
        }

        /* === ANTI-ZOOM iOS (paso de pago, movil) ===
           Safari/Chrome iOS hacen zoom al enfocar un control con font-size menor a 16px. En
           movil (menos de 1024px, donde vive el checkout del modal) forzamos 16px en TODOS los
           controles del panel de pago: inputs/select de PSE (buscador de banco, correo), campos
           merchant de la tarjeta (titular, documento) y el select del documento. kr-input-field
           y kr-field-component propagan la fuente a los iframes PCI (PAN/MM/CVV), que tampoco
           hacen zoom. Desktop (1024+) conserva sus tamanos aprobados (15px). */
        @media (max-width: 1023px) {
          .tikipal-payzen-inline input,
          .tikipal-payzen-inline select,
          .tikipal-payzen-inline textarea,
          .tikipal-payzen-inline .kr-embedded .kr-input-field,
          .tikipal-payzen-inline .kr-embedded .kr-field-component,
          .tikipal-payzen-inline .kr-embedded .kr-identity-document-type .kr-select-placeholder,
          .tikipal-payzen-inline .kr-embedded .kr-identity-document-type .kr-selected-option {
            font-size: 16px !important;
          }
        }
      `}</style>
      {payMethod === 'card' ? (
        <>
          <p className="mb-2.5 text-[14px] text-[#151515a3]">
            {isSpanish ? 'Tarjeta de crédito o débito' : 'Credit or debit card'} *
          </p>
          <PayzenCardForm formToken={formToken} displayMode="embedded-card" />
        </>
      ) : (
        <PsePaymentPanel
          billingInfo={billingInfo}
          orderId={orderId}
          eventId={eventId}
          spaceId={spaceId}
          entranceId={entranceId}
          timeSlotId={timeSlotId}
          quantity={quantity}
          isSpanish={isSpanish}
        />
      )}
    </div>
  );
};

export default PaymentMethodSelector;
